/**
 * artifactFilters — shared predicates for trimming an instance's artifact list
 * before it reaches a rendering surface.
 *
 * Consumed by both clients so the two stay in parity:
 *   - ora:     `WorkflowDetailDrawer.fetchArtifacts`
 *   - add-in:  `orchestratorApi.getArtifacts`
 *
 * CSV export was turned off product-wide (2026-08-13): the reviewer-facing
 * surfaces no longer offer a batch CSV, alongside `showExport={false}` on the
 * bulk review table. The artifact is still WRITTEN (`ebpu_export_proposals`
 * spills it to the `rpa-artifacts` container, and the completion recap parses
 * it server-side via `workflows.epic-policy-update-bulk.$instanceId.outcomes.api`)
 * — it is simply no longer listed for download.
 *
 * WHY THIS FILTERS AT THE FETCH SEAM, NOT INSIDE `WorkflowArtifactsSection`:
 * that component renders `artifacts.map((a, i) => ...)` and hands `i` to
 * `onArtifactClick(index)`, while the PARENT indexes its own copy of the array
 * to decide which artifact the lightbox opens. Filtering inside the component
 * would make those two arrays disagree and open the wrong artifact. Filtering
 * at the fetch keeps the rendered array and the indexed array the same object.
 * Keep it that way.
 */

/**
 * Artifact kinds that represent a downloadable CSV export.
 *
 * Both values come from the substrate's classifier
 * (`tools/artifacts.py:_kind_for_path`): `bulk_proposals_csv` for a
 * `bulk_proposals*.csv` (the EBPU batch export) and `csv` for anything else
 * with a `.csv` suffix.
 */
export const CSV_ARTIFACT_KINDS: ReadonlySet<string> = new Set([
  "csv",
  "bulk_proposals_csv",
]);

/** True when `kind` names a downloadable CSV export artifact. */
export function isCsvArtifactKind(kind: string | null | undefined): boolean {
  return !!kind && CSV_ARTIFACT_KINDS.has(kind);
}

/**
 * Return `list` without its CSV-export entries, preserving order.
 *
 * Generic over the element type so both `WorkflowArtifact` (ora) and
 * `ArtifactDto` (add-in) pass through without a cast. Pure — never mutates the
 * input. A missing/undefined `kind` is kept: unclassified artifacts are not
 * CSVs, and dropping them would hide evidence.
 */
export function excludeCsvArtifacts<T extends { kind?: string | null }>(
  list: readonly T[],
): T[] {
  return list.filter((a) => !isCsvArtifactKind(a.kind));
}
